import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useCompleteFragment } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import { Button, Stack, Text, toast } from '@eluve/components';
import { appointmentTranscriptAndSegmentsFragment } from '@eluve/frontend-appointment-hooks';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

import { useAppointmentParams } from '../useAppointmentParams';

import { useSummary } from './useSummary';

export const SummaryContent: React.FC = () => {
  const { appointmentId, tenantId } = useAppointmentParams();
  const [isScheduling, setIsScheduling] = useState(false);
  const client = useApiClient();

  const { llmSummary, llmOutputId } = useSummary({
    appointmentId,
  });

  const transcriptData = useCompleteFragment({
    fragment: appointmentTranscriptAndSegmentsFragment,
    key: {
      id: appointmentId,
    },
    strict: false,
  });

  const appointmentHasTranscript = transcriptData?.segments.length;

  const scheduleGroundednessEval = async () => {
    if (!llmOutputId) {
      return;
    }

    try {
      setIsScheduling(true);
      await client.reviewer.scheduleGroundednessEval({
        params: { tenantId, appointmentId },
        body: {
          llmOutputId,
        },
      });
    } catch {
      toast.error('Failed to schedule groundedness eval');
    } finally {
      setIsScheduling(false);
    }
  };

  if (!llmSummary) {
    return (
      <Stack gap={2}>
        <Text size="md">No LLM summary available for this appointment.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Stack
        align="start"
        className="p-4 max-sm:flex-col"
        gap={4}
        direction="horizontal"
      >
        <Stack gap={4} className="max-sm:order-last">
          <DynamicArtifactRenderer
            isReadonly={true}
            blocks={llmSummary.blocks}
          />
        </Stack>
        <SimpleTooltip
          label={
            !llmOutputId
              ? 'Summary has no llm output'
              : !appointmentHasTranscript
                ? 'Appointment has no transcript'
                : ''
          }
        >
          <Button
            variant="outline"
            loading={isScheduling}
            onClick={scheduleGroundednessEval}
            disabled={!llmOutputId || !appointmentHasTranscript}
          >
            Schedule Groundedness Eval
          </Button>
        </SimpleTooltip>
      </Stack>
    </Stack>
  );
};
